import { useRef } from 'react';

/**
 * Custom hook that returns a mutable ref object that always holds the last value passed to it.
 *
 * @template T - The type of the value stored in the ref.
 * @param value - The value to be stored in the ref.
 * @returns A mutable ref object that always holds the last value passed to hook.
 */
export default function useLastRef<T>(value: T) {
    const ref = useRef<T>(value);
    ref.current = value;
    return ref;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('useLastRef', async () => {
        const { renderHook } = await import('@testing-library/react-hooks');

        it('updates the value immediately', () => {
            const firstValue = { v: 1 };

            const { result, rerender } = renderHook((value) => useLastRef(value), {
                initialProps: firstValue,
            });

            expect(result.current.current).toBe(firstValue);

            const secondValue = { v: 2 };
            rerender(secondValue);

            expect(result.current.current).toBe(secondValue);
        });
    });
}

// #endregion
